import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/ApiService';
import ModalShell from './ModalShell';
import Chip from './Chip';
import { showErrorToast, showSuccessToast } from './toast';
import { getPOIDisplayName, getPOISpaceName } from '../browser/usePOIHierarchies';
import {
  DEVICE_STATUS_COLORS,
  DEVICE_STATUS_LABELS,
  type DeviceStatus,
} from '../../constants/dtStatus';

/**
 * "Add IoT device" — THE sheet behind every entry point that binds a device to an asset:
 * the viewer's double-click element menu, a Browser pane row's ⋯ menu, and (through the
 * same hook) any future surface.
 *
 * TWO PATHS, ONE SHEET (docs/external_files/mabat3d-dt-design-mockup.html → "Add IoT device"):
 *   attach — an existing device in this site that watches nothing yet
 *   create — a new device, registered and bound in one POST
 *
 * WHAT IT BINDS TO: an ASSET (a point_of_interests row), never a raw model element. The
 * write is a row in iot_device_pois (migration 043) plus the deprecated iot_devices.poi_id
 * mirror, both from server/utils/dtJunctions.js — so an element with no asset must have one
 * created FIRST. That step is not this component's: useAddIoTDevice runs the viewer's own
 * create-asset handler and only then opens this sheet, with `assetWasCreated` set so a
 * cancel can undo it (the mockup's "one undo").
 *
 * Deliberate deviation from the mockup: the ingest token is shown AFTER the device is
 * created, not before. The token is minted server-side (routeHelpers.newMachineToken) and
 * the client must not invent one — a pre-save token in the form would be a lie the moment
 * the request failed.
 */

export interface AddIoTDeviceTarget {
  /** Site id — every IoT route is site-scoped (`/assets/:siteId/iot-devices…`). */
  siteId: string;
  /** The ASSET (POI row) the device binds to. */
  poi: any;
  /** Model-element context for the header line, when the sheet came from a viewer. */
  element?: { idLabel?: string | null; model?: string | null } | null;
  /**
   * True when THIS flow created the asset a moment ago. Cancelling then removes it again,
   * so a user who backs out is not left with an asset they never asked for.
   */
  assetWasCreated?: boolean;
}

export interface AddIoTDeviceDismissal {
  /** A device was attached or created + bound — the host should reload its data. */
  bound: boolean;
}

interface AddIoTDeviceModalProps {
  target: AddIoTDeviceTarget;
  onDismiss: (result: AddIoTDeviceDismissal) => void;
}

/** The device row shape this sheet reads (server derives `status`; `poi_ids` is the junction). */
interface AttachableDevice {
  id: number;
  name: string;
  external_key: string | null;
  adapter: string;
  location_note: string | null;
  status: DeviceStatus;
  last_metric?: string | null;
  last_value?: number | null;
  last_ts?: string | null;
  poi_ids?: number[];
}

const INGEST_HINT = 'POST /api/iot/ingest · header X-Ingest-Token';

const noteBox: React.CSSProperties = {
  background: 'var(--dt-surface-2)',
  border: '1px solid var(--dt-border)',
  borderRadius: 9,
  padding: '9px 11px',
  fontSize: '.78rem',
  color: 'var(--dt-text-2)',
  lineHeight: 1.5,
};

const warnBox: React.CSSProperties = {
  ...noteBox,
  background: 'var(--dt-warn-t)',
  borderColor: 'var(--dt-warn)',
  color: 'var(--dt-text-2)',
};

const okBox: React.CSSProperties = {
  ...noteBox,
  background: 'var(--dt-good-t)',
  borderColor: 'var(--dt-good)',
};

const lastReadingText = (device: AttachableDevice): string => {
  if (!device.last_ts) return 'never reported';
  return `last reading ${device.last_metric} = ${device.last_value} · ${new Date(device.last_ts).toLocaleString()}`;
};

const AddIoTDeviceModal: React.FC<AddIoTDeviceModalProps> = ({ target, onDismiss }) => {
  const { siteId, poi, element, assetWasCreated } = target;
  const poiId = poi?.id;
  const assetName = getPOIDisplayName(poi);
  const assetLocation = getPOISpaceName(poi);

  const [tab, setTab] = useState<'attach' | 'create'>('attach');
  const [scope, setScope] = useState<'unbound' | 'all'>('unbound');
  const [query, setQuery] = useState('');
  const [devices, setDevices] = useState<AttachableDevice[] | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | 'create' | null>(null);
  const [form, setForm] = useState({ name: '', externalKey: '', locationNote: '' });
  // Set once a device is created: the ONE moment its ingest token is readable in full.
  const [created, setCreated] = useState<{ name: string; token: string } | null>(null);
  // Any successful write disarms the cancel-undo: the asset now has a device on it.
  const [bound, setBound] = useState(false);

  useEffect(() => {
    if (tab !== 'attach') return undefined;
    let stale = false;
    setDevices(null);
    apiService.get(`/assets/${siteId}/iot-devices${scope === 'unbound' ? '?binding=unbound' : ''}`)
      .then((list: any) => { if (!stale) setDevices(Array.isArray(list) ? list : []); })
      .catch((err: any) => {
        if (stale) return;
        setDevices([]);
        setErrorMsg(err?.message || 'Failed to load devices');
      });
    return () => { stale = true; };
  }, [siteId, scope, tab]);

  const visibleDevices = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return devices || [];
    return (devices || []).filter(d =>
      d.name.toLowerCase().includes(needle) || (d.external_key || '').toLowerCase().includes(needle));
  }, [devices, query]);

  const isBoundHere = useCallback(
    (device: AttachableDevice) => (device.poi_ids || []).some(id => String(id) === String(poiId)),
    [poiId]
  );

  const attach = useCallback(async (device: AttachableDevice) => {
    setBusy(device.id);
    setErrorMsg(null);
    try {
      await apiService.post(`/assets/${siteId}/iot-devices/${device.id}/pois`, { poiId });
      setBound(true);
      showSuccessToast(`"${device.name}" now watches "${assetName}"`);
      onDismiss({ bound: true });
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to attach the device');
    } finally {
      setBusy(null);
    }
  }, [siteId, poiId, assetName, onDismiss]);

  /** Cut ONE binding (the device and its other assets survive — see the unbind route). */
  const detach = useCallback(async (device: AttachableDevice) => {
    setBusy(device.id);
    setErrorMsg(null);
    try {
      await apiService.delete(`/assets/${siteId}/iot-devices/${device.id}/pois/${poiId}`);
      setDevices(prev => (prev || []).map(d => (d.id === device.id
        ? { ...d, poi_ids: (d.poi_ids || []).filter(id => String(id) !== String(poiId)) }
        : d)));
      showSuccessToast(`"${device.name}" no longer watches "${assetName}"`);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to detach the device');
    } finally {
      setBusy(null);
    }
  }, [siteId, poiId, assetName]);

  const createAndBind = useCallback(async () => {
    const name = form.name.trim();
    if (!name) {
      setErrorMsg('Name is required');
      return;
    }
    setBusy('create');
    setErrorMsg(null);
    try {
      // One POST: the device is registered AND bound (poiId), so a failure cannot leave an
      // unbound orphan behind — which is the whole reason this flow exists.
      const device = await apiService.post(`/assets/${siteId}/iot-devices`, {
        name,
        externalKey: form.externalKey.trim() || null,
        locationNote: form.locationNote.trim() || null,
        poiId,
      });
      setBound(true);
      setCreated({ name, token: device?.ingest_token || '' });
      showSuccessToast(`Created "${name}" on "${assetName}"`);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to create the device');
    } finally {
      setBusy(null);
    }
  }, [siteId, poiId, assetName, form]);

  const copy = (text: string, what: string) => {
    if (!navigator.clipboard?.writeText) {
      showErrorToast('Clipboard is not available in this browser');
      return;
    }
    navigator.clipboard.writeText(text)
      .then(() => showSuccessToast(`Copied ${what}`))
      .catch(() => showErrorToast(`Failed to copy ${what}`));
  };

  const close = () => onDismiss({ bound });

  return (
    <ModalShell onClose={close} width={520}>
      <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--dt-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>📡 Add IoT device</span>
        {element?.idLabel && (
          <span className="dt-pill2">from element {element.idLabel}</span>
        )}
      </div>

      {/* What the sheet was opened with — the asset the device will bind to. */}
      <div className="dt-ctx" style={{ marginTop: 12 }}>
        <b>{assetName}</b> <span className="dt-mono">#{String(poiId)}</span>
        {assetLocation && <> · <b>location:</b> {assetLocation}</>}
        {element?.model && <> · <b>model:</b> {element.model}</>}
        {element?.idLabel && <> · <span className="dt-mono">{element.idLabel}</span></>}
      </div>

      {assetWasCreated && (
        <div style={{ ...warnBox, marginBottom: 12 }}>
          <b>This element had no asset.</b> A device binds to an asset, never to a raw model
          element — so the asset <b>{assetName}</b> was created first and the device is bound to
          it in the same flow. Cancelling now removes that asset again.
        </div>
      )}

      {!created && (
        <div style={{ display: 'flex', gap: 6, margin: '12px 0' }}>
          <button type="button" className={`dt-btn sm${tab === 'attach' ? ' pri' : ''}`} style={{ flex: 1 }}
            onClick={() => { setTab('attach'); setErrorMsg(null); }}>
            Attach existing device
          </button>
          <button type="button" className={`dt-btn sm${tab === 'create' ? ' pri' : ''}`} style={{ flex: 1 }}
            onClick={() => { setTab('create'); setErrorMsg(null); }}>
            Create new device
          </button>
        </div>
      )}

      {errorMsg && (
        <div style={{ color: 'var(--dt-bad)', fontSize: '.83rem', margin: '0 0 10px' }}>{errorMsg}</div>
      )}

      {/* ── PATH A — attach an existing device ─────────────────────────────── */}
      {!created && tab === 'attach' && (
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input
              className="dt-field"
              style={{ flex: 1 }}
              placeholder="Search by name or external key…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <button type="button" className={`dt-pill${scope === 'unbound' ? ' active' : ''}`}
              onClick={() => setScope('unbound')}>Unbound only</button>
            <button type="button" className={`dt-pill${scope === 'all' ? ' active' : ''}`}
              onClick={() => setScope('all')}>All</button>
          </div>

          {devices === null ? (
            <div className="dt-muted">Loading devices…</div>
          ) : visibleDevices.length === 0 ? (
            <div className="dt-statebox">
              {scope === 'unbound'
                ? 'No unbound devices in this site — create one instead.'
                : 'No devices match this search.'}
            </div>
          ) : visibleDevices.map(device => (
            <div key={device.id} className="dt-drow">
              <div className="dt-dtop">
                <span className="dt-dnm">{device.name}</span>
                <Chip text={DEVICE_STATUS_LABELS[device.status] || device.status} color={DEVICE_STATUS_COLORS[device.status] || 'var(--dt-text-3)'} />
                {device.external_key && <span className="dt-dkey">ext: {device.external_key}</span>}
                <span className="dt-dact">
                  {isBoundHere(device) ? (
                    <>
                      <span className="dt-pill2">Already here</span>
                      <button type="button" className="dt-btn sm" disabled={busy !== null}
                        onClick={() => void detach(device)}>
                        {busy === device.id ? 'Detaching…' : 'Detach'}
                      </button>
                    </>
                  ) : (
                    <button type="button" className="dt-btn sm pri" disabled={busy !== null}
                      onClick={() => void attach(device)}>
                      {busy === device.id ? 'Attaching…' : 'Attach'}
                    </button>
                  )}
                </span>
              </div>
              <div className="dt-dmeta">
                {device.adapter} adapter
                {device.location_note && <> · {device.location_note}</>}
                {' · '}{lastReadingText(device)}
                {(device.poi_ids?.length || 0) > 0 && <> · watches {device.poi_ids!.length} asset(s)</>}
              </div>
            </div>
          ))}

          <div style={{ ...noteBox, marginTop: 11 }}>
            Only devices in <b>this site</b> are listed; <b>Unbound only</b> hides the ones already
            watching something. A device may watch several assets — <span className="dt-mono">iot_device_pois</span> is
            many-to-many — so switch to <b>All</b> to add this asset to one that is already in use.
          </div>
        </div>
      )}

      {/* ── PATH B — create a new device ───────────────────────────────────── */}
      {!created && tab === 'create' && (
        <div>
          <div className="dt-grid2" style={{ marginBottom: 10 }}>
            <div>
              <label className="dt-flabel">Name *</label>
              <input className="dt-field" maxLength={200} autoFocus
                placeholder={`${assetName} sensor`}
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="dt-flabel">Adapter *</label>
              <select className="dt-field" value="generic" disabled onChange={() => {}}>
                <option value="generic">generic — token + JSON payload</option>
              </select>
              <div style={{ fontSize: '.72rem', color: 'var(--dt-text-3)', marginTop: 4 }}>
                Only <span className="dt-mono">generic</span> exists today; vendor adapters extend this list.
              </div>
            </div>
          </div>
          <div className="dt-grid2" style={{ marginBottom: 10 }}>
            <div>
              <label className="dt-flabel">External key</label>
              <input className="dt-field" maxLength={200} placeholder="ahu214-01"
                value={form.externalKey} onChange={e => setForm({ ...form, externalKey: e.target.value })} />
              <div style={{ fontSize: '.72rem', color: 'var(--dt-text-3)', marginTop: 4 }}>
                The id the device puts in its payload. Leave empty to match on token only.
              </div>
            </div>
            <div>
              <label className="dt-flabel">Location note</label>
              <input className="dt-field" maxLength={300} placeholder="Roof plant, north end"
                value={form.locationNote} onChange={e => setForm({ ...form, locationNote: e.target.value })} />
            </div>
          </div>
          <div style={okBox}>
            Binds to <b>{assetName}</b> (asset #{String(poiId)}) on save. Readings land on the asset,
            so the element badge, the room and the system diagram all see them without another link.
            The ingest token is minted on save and shown once, right here.
          </div>
        </div>
      )}

      {/* ── After a create: the token, in full, exactly once ───────────────── */}
      {created && (
        <div>
          <div style={{ ...okBox, marginBottom: 12 }}>
            <b>{created.name}</b> is registered and bound to <b>{assetName}</b>.
          </div>
          <label className="dt-flabel">Ingest token</label>
          <div className="dt-dtok" style={{ marginTop: 0 }}>
            <code>{created.token || '—'}</code>
            <button type="button" className="dt-btn sm" disabled={!created.token}
              onClick={() => copy(created.token, 'the ingest token')}>Copy</button>
          </div>
          <div className="dt-dtok">
            <span className="msk">{INGEST_HINT}</span>
            <button type="button" className="dt-btn sm"
              onClick={() => copy(`${INGEST_HINT}: ${created.token}`, 'the ingest details')}>Copy</button>
          </div>
          <div style={{ fontSize: '.72rem', color: 'var(--dt-text-3)', margin: '6px 0 0' }}>
            Shown in full only here. Afterwards it is revealed (or rotated) from the IoT Devices page.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <button type="button" className="dt-btn sm" onClick={close}>
          {created || bound ? 'Done' : 'Cancel'}
        </button>
        {!created && tab === 'create' && (
          <button type="button" className="dt-btn sm pri" disabled={busy !== null}
            onClick={() => void createAndBind()}>
            {busy === 'create' ? 'Creating…' : 'Create & bind device'}
          </button>
        )}
      </div>
    </ModalShell>
  );
};

export default AddIoTDeviceModal;
